import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { ResetTaskService } from '../reset-task-service';
import type { ResetTaskApi } from '../../apis/reset-task-api';
import { ResetTaskApi as ResetTaskApiToken } from '../../apis/reset-task-api';
import type { NoveltyCheckTaskStatusService } from '../task-status-service';
import { NoveltyCheckTaskStatusService as TaskStatusServiceToken } from '../task-status-service';

/**
 * 重置任务服务实现
 * @description 实现重置查新任务的业务逻辑
 */
@injectable()
export class ResetTaskServiceImpl implements ResetTaskService {
    constructor(
        @inject(ResetTaskApiToken)
        private _resetTaskApi: ResetTaskApi,
        @inject(TaskStatusServiceToken)
        private _taskStatusService: NoveltyCheckTaskStatusService,
    ) {}

    /**
     * 重置任务
     * @param taskId 任务ID
     * @returns 操作是否成功
     * @description 执行任务重置操作，包括调用API和更新本地状态
     */
    async resetTask(taskId: string): Promise<boolean> {
        try {
            // 检查任务是否可以重置
            const canReset = await this.canResetTask(taskId);
            if (!canReset) {
                console.warn(`Task ${taskId} cannot be reset in current state`);
                return false;
            }

            // 调用API重置任务
            const response = await this._resetTaskApi.resetTask({
                task_id: taskId,
            });

            // 重置成功后，清理本地任务状态
            if (response.success !== false) {
                // 这里可以添加清理本地状态的逻辑
                // 例如：清理缓存、重置模型状态等
                console.log(`Task ${taskId} reset successfully`);
                return true;
            }

            return false;
        } catch (error) {
            console.error('Failed to reset task:', error);
            return false;
        }
    }

    /**
     * 检查任务是否可以重置
     * @param taskId 任务ID
     * @returns 是否可以重置
     * @description 根据任务状态判断是否允许重置
     */
    async canResetTask(taskId: string): Promise<boolean> {
        try {
            // 获取当前任务状态
            const taskStatus = await this._taskStatusService.pullTaskStatus();

            // 根据业务规则判断是否可以重置
            // 这里可以根据具体的业务需求来定义重置规则
            // 例如：只有在特定状态下才能重置任务
            return taskStatus !== undefined;
        } catch (error) {
            console.error('Failed to check task reset status:', error);
            return false;
        }
    }
}
